import { DeviceSettings, IridiumAccount, RadioChannel, RadioRegion } from "@somewear/api";
import type {
	ButtonFunctionMap,
	InmarsatLeaseTypeMap,
	RadioModeMap,
	RadioPowerModeMap,
} from "@somewear/api/src/proto/command_public_proto_pb";
import {
	ButtonFunction,
	InmarsatLeaseType,
	RadioBandwidth,
	RadioMode,
	RadioPowerMode,
	RadioSpreadFactor,
} from "@somewear/api/src/proto/command_public_proto_pb";
import type { MeshDataRateParams, ValueOf } from "@somewear/model";
import { hzToMhz, MeshDataRateIds, TrackingIntervalIds } from "@somewear/model";

export interface IDeviceSettings
	extends Omit<
		DeviceSettings.AsObject,
		| "meshToggle"
		| "radioMode"
		| "radioPowerMode"
		| "lowSpeedFrequencyHz"
		| "highSpeedFrequencyHz"
		| "meshDataRateParameters"
	> {}

export interface SelectOption<T> {
	id: string;
	value: T;
	label: string;
	subLabel?: string;
	hideFromSearch?: boolean;
	group?: string;
	networkTypes?: NetworkType[];
	disabled?: boolean;
	type?: IntervalCategory;
}

export enum IntervalCategory {
	Somewear = "Somewear",
	ATAK = "ATAK",
	/** Legacy intervals will be displayed if they are the current value on a device, however
	 * they are not available to be set as new values.
	 */
	Legacy = "Legacy",
}

export interface IIntervalSelect {
	id: string;
	value: TrackingInterval;
	label: string;
	subLabel?: string;
	group?: string;
}

export enum NetworkType {
	Mesh = "mesh",
	Satellite = "satellite",
	SatteliteLite = "sattelite-lite",
}

export interface TrackingInterval {
	id: TrackingIntervalIds;
	logRate: number;
	shareRate: number;
	type: IntervalCategory;
	label: string;
	subLabel?: string;
	networkTypes?: NetworkType[];
}

export type SplitIntervalValues = {
	mesh: TrackingIntervalIds | undefined;
	sat: TrackingIntervalIds | undefined;
};

export const trackingIntervals: TrackingInterval[] = [
	{
		id: TrackingIntervalIds.OneSec,
		logRate: 1,
		shareRate: 1,
		type: IntervalCategory.Legacy,
		label: "1s",
		subLabel: "Logs: 1s | Shares: 1s",
	},
	{
		id: TrackingIntervalIds.TwoSec,
		logRate: 2,
		shareRate: 2,
		type: IntervalCategory.Legacy,
		label: "2s",
		subLabel: "Logs: 2s | Shares: 2s",
	},
	{
		id: TrackingIntervalIds.ATAK5,
		logRate: 5,
		shareRate: 5,
		type: IntervalCategory.ATAK,
		label: "5s",
		networkTypes: [NetworkType.Mesh, NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK15,
		logRate: 15,
		shareRate: 15,
		type: IntervalCategory.ATAK,
		label: "15s",
		networkTypes: [NetworkType.Mesh, NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK30,
		logRate: 30,
		shareRate: 30,
		type: IntervalCategory.ATAK,
		label: "30s",
		networkTypes: [NetworkType.Mesh, NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK45,
		logRate: 45,
		shareRate: 45,
		type: IntervalCategory.Legacy,
		label: "45s",
	},
	{
		id: TrackingIntervalIds.ATAK1Min,
		logRate: 60,
		shareRate: 60,
		type: IntervalCategory.ATAK,
		label: "1min",
		networkTypes: [NetworkType.Mesh, NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK2Min,
		logRate: 120,
		shareRate: 120,
		type: IntervalCategory.ATAK,
		label: "2min",
		networkTypes: [NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK5Min,
		logRate: 300,
		shareRate: 300,
		type: IntervalCategory.ATAK,
		label: "5min",
		networkTypes: [NetworkType.Mesh, NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK10Min,
		logRate: 600,
		shareRate: 600,
		type: IntervalCategory.ATAK,
		label: "10min",
		networkTypes: [NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK20Min,
		logRate: 1200,
		shareRate: 1200,
		type: IntervalCategory.ATAK,
		label: "20min",
		networkTypes: [NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK30Min,
		logRate: 1800,
		shareRate: 1800,
		type: IntervalCategory.ATAK,
		label: "30min",
		networkTypes: [NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.ATAK1Hr,
		logRate: 3600,
		shareRate: 3600,
		type: IntervalCategory.ATAK,
		label: "1hr",
		networkTypes: [NetworkType.Satellite],
	},
	{
		id: TrackingIntervalIds.TwoSec,
		logRate: 2,
		shareRate: 2,
		type: IntervalCategory.Legacy,
		label: "2s",
		subLabel: "Logs: 2s | Shares: 2s",
	},
	{
		id: TrackingIntervalIds.OnFoot,
		logRate: 30,
		shareRate: 60,
		type: IntervalCategory.Legacy,
		label: "On-Foot",
		subLabel: "Logs: 30s | Shares: 1m",
	},
	{
		id: TrackingIntervalIds.Vehicle,
		logRate: 15,
		shareRate: 30,
		type: IntervalCategory.Legacy,
		label: "Vehicle",
		subLabel: "Logs: 15s | Shares: 30s",
	},
	{
		id: TrackingIntervalIds.OneMin,
		logRate: 60,
		shareRate: 60,
		type: IntervalCategory.Legacy,
		label: "Custom",
		subLabel: "Logs: 1m | Shares: 1m",
	},
	{
		id: TrackingIntervalIds.Custom1,
		logRate: 30,
		shareRate: 300,
		type: IntervalCategory.Legacy,
		label: "Custom",
		subLabel: "Logs: 30s | Shares: 5m",
	},
	{
		id: TrackingIntervalIds.Custom2,
		logRate: 60,
		shareRate: 600,
		type: IntervalCategory.Legacy,
		label: "Custom",
		subLabel: "Logs: 1m | Shares: 10m",
	},
];

export function gatewayStatusToString(
	status: IridiumAccount.StatusMap[keyof IridiumAccount.StatusMap] | undefined,
) {
	const defaultValue = "Unknown";
	if (status === undefined) return defaultValue;
	if (status === IridiumAccount.Status.ACTIVE) return "Active";
	if (status === IridiumAccount.Status.DEACTIVE) return "Inactive";
	if (status === IridiumAccount.Status.SUSPENDED) return "Suspended";

	return defaultValue;
}

export interface RadioFrequency {
	low: number;
	high: number;
	id: string;
	label: string;
}

function formatFrequency(freq: number) {
	return `${freq / 1000000} MHz`;
}

export function displayFrequency(frequencyType: RadioFrequency) {
	return `${formatFrequency(frequencyType.low)}, ${formatFrequency(frequencyType.high)}`;
}

export interface IRadioPowerMode {
	label: string;
	id: string;
	value: ValueOf<RadioPowerModeMap>;
}

export interface IButtonFunction {
	label: string;
	id: string;
	value: ValueOf<ButtonFunctionMap>;
	subLabel?: string;
}

export interface IInmarsatLeaseType {
	label: string;
	id: string;
	value: ValueOf<InmarsatLeaseTypeMap>;
}

export interface IRadioMode {
	value: ValueOf<RadioModeMap>;
}

export interface IMeshDataRate {
	label: string;
	id: MeshDataRateIds;
	value: MeshDataRateParams;
}

export const meshDataRates: IMeshDataRate[] = [
	{
		label: "Low",
		id: MeshDataRateIds.Low,
		value: {
			spreadFactor: RadioSpreadFactor.RADIOSPREADFACTOR08,
			bandwidth: RadioBandwidth.RADIOBANDWIDTH250KHZ,
		},
	},
	{
		label: "High",
		id: MeshDataRateIds.High,
		value: {
			spreadFactor: RadioSpreadFactor.RADIOSPREADFACTOR05,
			bandwidth: RadioBandwidth.RADIOBANDWIDTH500KHZ,
		},
	},
];

export const spreadFactorOptions = [
	{ value: RadioSpreadFactor.RADIOSPREADFACTOR05, label: "SF5 (62.5 kbps)" },
	{ value: RadioSpreadFactor.RADIOSPREADFACTOR06, label: "SF6 (37.5 kbps)" },
	{ value: RadioSpreadFactor.RADIOSPREADFACTOR07, label: "SF7 (21.8 kbps)" },
	{ value: RadioSpreadFactor.RADIOSPREADFACTOR08, label: "SF8 (12.5 kbps)" },
	{ value: RadioSpreadFactor.RADIOSPREADFACTOR09, label: "SF9 (7 kbps)" },
	{ value: RadioSpreadFactor.RADIOSPREADFACTOR10, label: "SF10 (3.9 kbps)" },
	//
	// { value: RadioSpreadFactor.RADIOSPREADFACTOR11, label: "SF11 (2.1 kbps)" },
	// { value: RadioSpreadFactor.RADIOSPREADFACTOR12, label: "SF12 (1.2 kbps)" },
].map((opt) => ({ ...opt, id: opt.value.toString() }));

export const radioPowerModes: IRadioPowerMode[] = [
	{ label: "Low", id: "low", value: RadioPowerMode.RADIOPOWERMODELOW },
	{ label: "Medium", id: "medium", value: RadioPowerMode.RADIOPOWERMODEMEDIUM },
	{ label: "High", id: "high", value: RadioPowerMode.RADIOPOWERMODEHIGH },
];

export const buttonFunctions: IButtonFunction[] = [
	{ label: "Enable/Disable Satellite", id: "sat", value: ButtonFunction.BUTTONFUNCTIONSAT },
	{
		label: "Enable/Disable Tracking",
		id: "tracking",
		value: ButtonFunction.BUTTONFUNCTIONTRACKING,
	},
	{
		label: "Pair/Unpair a Health Sensor",
		id: "sensor",
		value: ButtonFunction.BUTTONFUNCTIONSENSOR,
	},
	{
		label: "Push to Talk",
		id: "ptt",
		value: ButtonFunction.BUTTONFUNCTIONPTT,
		subLabel: "Requires ATAK plugin v1.1.19",
	},
];

export const radioModes: IRadioMode[] = [
	{ value: RadioMode.RADIOMODENONE },
	{ value: RadioMode.RADIOMODEENABLED },
	{ value: RadioMode.RADIOMODEDISABLED },
	// { value: RadioMode.RADIOMODESTEALTH },
];

export const toTrackingIntervalSelectOption = (
	interval: TrackingInterval,
): SelectOption<TrackingInterval> => ({
	id: interval.id,
	label: interval.label,
	subLabel: interval.subLabel,
	value: interval,
	group: interval.type,
	networkTypes: interval.networkTypes,
	type: interval.type,
});

export const radioChannelToRadioChannelOption = (
	radioChannel: RadioChannel.AsObject,
	shouldCreateGroups: boolean,
): SelectOption<RadioChannel.AsObject> => ({
	id: radioChannel.id.toString(),
	label: radioChannel.name,
	value: radioChannel,
	subLabel: `${hzToMhz(radioChannel.lowSpeedFrequencyHz)} MHz, ${hzToMhz(radioChannel.highSpeedFrequencyHz)} MHz`,
	group: shouldCreateGroups
		? radioChannel.region === RadioRegion.RADIOREGIONUNLOCKED
			? "Custom Channels"
			: "Regional Channels"
		: undefined,
});

export const trackingIntervalOptions: SelectOption<TrackingInterval>[] = trackingIntervals.map(
	toTrackingIntervalSelectOption,
);

export const inmarsatLeaseTypes: IInmarsatLeaseType[] = [
	{ label: "LAISR", id: "laisr", value: InmarsatLeaseType.INMARSATLEASETYPELAISR },
	{ label: "LTAC", id: "ltac", value: InmarsatLeaseType.INMARSATLEASETYPELTAC },
];
